import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  Dispatch,
  useCallback,
  useMemo,
} from 'react'

import { PatchPurposeRequest, TPurposesResponse } from 'common/types'
import { useSetPurposesUS } from 'hooks/useSetPurposesUS'
import { TUserType } from 'contexts/app-context/types'

import { UseMutationResult } from '@tanstack/react-query'

interface RequestConfirmationBottomSheetProviderUSProps {
  showDialog: boolean
  showSuccessStep: boolean
  changingPurpose: PatchPurposeRequest
  setShowDialog: Dispatch<React.SetStateAction<boolean>>
  setShowSuccessStep: Dispatch<React.SetStateAction<boolean>>
  setChangingPurpose: Dispatch<React.SetStateAction<PatchPurposeRequest>>
  mutation: UseMutationResult<TPurposesResponse<TUserType>, unknown, PatchPurposeRequest, unknown>
}

interface RequestConfirmationBottomSheetContextUSProps {
  children: ReactNode
}

const RequestConfirmationBottomSheetContextUS =
  createContext<RequestConfirmationBottomSheetProviderUSProps>(
    {} as RequestConfirmationBottomSheetProviderUSProps,
  )

/**
 * Provider responsible for setting US users new preferences (useSetPurposesUS)
 *
 * @param {RequestConfirmationBottomSheetContextUSProps}
 * @returns {RequestConfirmationBottomSheetContextUS.Provider}
 */
export const RequestConfirmationBottomSheetProviderUS = ({
  children,
}: RequestConfirmationBottomSheetContextUSProps) => {
  const [showDialog, setShowDialog] = useState(false)
  const [showSuccessStep, setShowSuccessStep] = useState(false)
  const [changingPurpose, setChangingPurpose] = useState<PatchPurposeRequest>(
    {} as PatchPurposeRequest,
  )
  const showSuccessToggle = useCallback(() => {
    setShowSuccessStep((current) => !current)
  }, [setShowSuccessStep])

  const mutation = useSetPurposesUS({ showSuccessToggle })

  const value = useMemo(
    () => ({
      showDialog,
      setShowDialog,
      showSuccessStep,
      setShowSuccessStep,
      mutation,
      changingPurpose,
      setChangingPurpose,
    }),
    [
      showDialog,
      setShowDialog,
      showSuccessStep,
      setShowSuccessStep,
      mutation,
      changingPurpose,
      setChangingPurpose,
    ],
  )

  return (
    <RequestConfirmationBottomSheetContextUS.Provider value={value}>
      {children}
    </RequestConfirmationBottomSheetContextUS.Provider>
  )
}

export function useRequestConfirmationBottomSheetContextUS() {
  const context = useContext(RequestConfirmationBottomSheetContextUS)

  if (!context)
    throw new Error(
      'use RequestConfirmationBottomSheetProviderUS must be used within a RequestConfirmationBottomSheetProviderUS',
    )

  return context
}
